import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mail,
  Send,
  CheckCircle2,
  AlertCircle,
  Clock,
  Users,
  Loader2,
  History,
  Bot,
  MousePointerClick,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

/**
 * Page admin de gestion du brief matinal.
 *
 * Affiche :
 *  - État du scheduler (running, heure d'envoi, fuseau, prochain run)
 *  - Liste des destinataires détectés
 *  - Historique des 30 derniers envois
 *  - Bouton "Envoyer maintenant" (test manuel)
 *
 * Source : GET /api/admin/morning-brief/status, /logs, POST .../send-now
 */
export default function MorningBriefSettings() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/morning-brief/status"),
        api.get("/admin/morning-brief/logs?limit=30"),
      ]);
      setStatus(s.data);
      setLogs(l.data.logs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sendNow = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/admin/morning-brief/send-now");
      setLastResult(data);
      if (data.sent > 0) {
        toast.success(`Brief envoyé à ${data.sent} destinataire${data.sent > 1 ? "s" : ""}`);
      } else if (data.failed > 0) {
        toast.error(`Échec : ${data.failed} envoi(s) raté(s)`);
      } else {
        toast.info("Aucun destinataire (vérifie les utilisateurs field_operator)");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("fr-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-[#575A57]" data-testid="morning-brief-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        Chargement…
      </div>
    );
  }

  const sched = status?.scheduler || {};
  const recipients = status?.recipients || [];
  const lastSent = status?.last_sent;

  return (
    <div className="space-y-6" data-testid="morning-brief-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#8C6B2F]" />
          <h2 className="font-heading text-xl font-semibold text-[#1A1C1A]">
            Brief matinal du terrain
          </h2>
        </div>
        <Link
          to="/parametres"
          className="text-sm text-[#575A57] hover:text-[#1F4E3D] flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux paramètres
        </Link>
      </div>

      {/* État du scheduler */}
      <section className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <h3 className="font-heading text-base font-medium text-[#1A1C1A] mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#1F4E3D]" />
          État du planificateur
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="morning-brief-scheduler-status">
          <StatusCell
            label="Activé"
            value={sched.running ? "Oui" : "Non"}
            color={sched.running ? "#1F4E3D" : "#A3A6A3"}
            icon={sched.running ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
          />
          <StatusCell
            label="Heure d'envoi"
            value={`${String(sched.send_hour ?? 7).padStart(2, "0")}h00`}
            color="#1A1C1A"
          />
          <StatusCell
            label="Fuseau"
            value={sched.timezone || "Europe/Zurich"}
            color="#575A57"
          />
          <StatusCell
            label="Prochain envoi"
            value={sched.next_run_at ? fmtDate(sched.next_run_at) : "—"}
            color="#575A57"
          />
        </div>
        {!sched.configured && (
          <p className="text-xs text-[#A04E2C] bg-[#FBEEEA] border border-[#E8B0A0] rounded p-2 mt-3" data-testid="morning-brief-not-configured-warning">
            ⚠️ <strong>Resend non configuré</strong> en environnement <em>preview</em>. Les variables <code className="font-mono">RESEND_API_KEY</code> et <code className="font-mono">SENDER_EMAIL</code> doivent être définies (déjà OK en production maison-thai.ch). Le scheduler activera automatiquement dès qu'elles seront détectées au redémarrage.
          </p>
        )}
        <p className="text-xs text-[#575A57] mt-3">
          💡 Pour modifier l'heure d'envoi, ajustez la variable d'environnement <code className="font-mono">MORNING_BRIEF_HOUR</code> (ex: <code>6</code> pour 6h, défaut <code>7</code>).
        </p>
      </section>

      {/* Destinataires */}
      <section className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <h3 className="font-heading text-base font-medium text-[#1A1C1A] mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-[#1F4E3D]" />
          Destinataires actuels ({recipients.length})
        </h3>
        {recipients.length === 0 ? (
          <p className="text-sm text-[#575A57]" data-testid="morning-brief-no-recipients">
            Aucun destinataire détecté. Créez au moins un utilisateur avec le rôle <code>field_operator</code> (ou <code>admin</code>), ou définissez <code className="font-mono">MORNING_BRIEF_RECIPIENTS</code> dans l'environnement.
          </p>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="morning-brief-recipients-list">
            {recipients.map((r, i) => (
              <li
                key={i}
                className="flex items-center gap-2 bg-[#F4F4F0] border border-[#E5E5E0] rounded px-3 py-1.5 text-sm"
              >
                <Mail className="w-3.5 h-3.5 text-[#1F4E3D] flex-shrink-0" />
                <span className="font-medium text-[#1A1C1A]">{r.name}</span>
                <span className="text-[#575A57] truncate font-mono text-xs">— {r.email}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Action manuelle */}
      <section className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <h3 className="font-heading text-base font-medium text-[#1A1C1A] mb-3 flex items-center gap-2">
          <Send className="w-4 h-4 text-[#1F4E3D]" />
          Envoyer le brief maintenant
        </h3>
        <p className="text-sm text-[#575A57] mb-3">
          Permet de tester l'envoi sans attendre le cron quotidien. Génère le brief actuel (DLC + Réappro) et l'envoie aux {recipients.length} destinataires.
        </p>
        <Button
          onClick={sendNow}
          disabled={sending || recipients.length === 0}
          data-testid="morning-brief-send-now-btn"
          className="bg-[#1F4E3D] hover:bg-[#173B2E]"
        >
          {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
          Envoyer maintenant
        </Button>

        {lastResult && (
          <div
            className={`mt-4 rounded p-3 text-sm border ${
              lastResult.sent > 0 ? "bg-[#E8F2EC] border-[#A8C9B6] text-[#1F4E3D]" : "bg-[#FBEEEA] border-[#E8B0A0] text-[#A04E2C]"
            }`}
            data-testid="morning-brief-last-result"
          >
            <strong>{lastResult.sent} envoyé(s)</strong> ·{" "}
            <strong>{lastResult.failed} échec(s)</strong> ·{" "}
            <strong>{lastResult.total_tasks} tâche(s) dans le brief</strong>
            {lastResult.recipients && lastResult.recipients.some((r) => !r.success) && (
              <ul className="mt-2 ml-4 list-disc text-xs">
                {lastResult.recipients
                  .filter((r) => !r.success)
                  .map((r, i) => (
                    <li key={i} className="font-mono">
                      {r.to} : {r.error}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {/* Historique */}
      <section className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <h3 className="font-heading text-base font-medium text-[#1A1C1A] mb-3 flex items-center gap-2">
          <History className="w-4 h-4 text-[#1F4E3D]" />
          Historique des envois ({logs.length})
        </h3>
        {logs.length === 0 ? (
          <p className="text-sm text-[#575A57]">Aucun envoi enregistré pour l'instant.</p>
        ) : (
          <div className="border border-[#E5E5E0] rounded max-h-96 overflow-y-auto" data-testid="morning-brief-logs">
            {logs.map((log) => (
              <div
                key={log.id}
                className="px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 text-sm flex items-center gap-3 flex-wrap hover:bg-[#F9F9F6]"
                data-testid={`morning-brief-log-${log.id}`}
              >
                {log.sent > 0 ? (
                  <CheckCircle2 className="w-4 h-4 text-[#1F4E3D] flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-[#C05C47] flex-shrink-0" />
                )}
                <span className="text-xs font-mono text-[#575A57] w-28 flex-shrink-0">
                  {fmtDate(log.sent_at)}
                </span>
                <Badge
                  variant="outline"
                  className={`text-[10px] ${log.source === "scheduler" ? "bg-[#F1EAF8] text-[#5B3A8C] border-[#C9B5E0]" : "bg-[#E8EEF6] text-[#1F3A66] border-[#A8BBD6]"}`}
                >
                  {log.source === "scheduler" ? (
                    <><Bot className="w-3 h-3 mr-1" /> Cron</>
                  ) : (
                    <><MousePointerClick className="w-3 h-3 mr-1" /> Manuel</>
                  )}
                </Badge>
                <span className="text-xs text-[#1A1C1A] flex-1 min-w-[200px]">
                  <strong className="text-[#1F4E3D]">{log.sent}</strong> envoyé(s) ·
                  {log.failed > 0 && <strong className="text-[#C05C47] ml-1">{log.failed} échec(s) ·</strong>}
                  <span className="ml-1">{log.total_tasks} tâche(s)</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatusCell({ label, value, color, icon }) {
  return (
    <div className="bg-[#F4F4F0] border border-[#E5E5E0] rounded p-2">
      <div className="text-[10px] uppercase tracking-wider text-[#575A57]">{label}</div>
      <div className="text-sm font-mono mt-0.5 flex items-center gap-1.5" style={{ color }}>
        {icon}
        {value}
      </div>
    </div>
  );
}
